/**
 * usePlacementSizeChange -- change a saved placement's size, its location,
 * or both, and ask first when the store's client has not approved the
 * resulting size.
 *
 * Jake, 2026-10-27:
 *   "the dimensions in the right column should be editable within the
 *    allowed size for the placement. If the size change requires an
 *    approval, show this in a modal dialog, along with the same
 *    distributor-approval-override affordance we have in the embroidery
 *    upload dialog in the decoration library."
 *   "When changing a placement location ... the placement dimensions should
 *    be updated to the default dimensions ... If this requires a new
 *    digitization and approval, warn the user in a modal with the option to
 *    keep the digitized/approved dimensions used in the previous placement
 *    location, or to override."
 *
 * One flow serves both the inspector's size inputs and the location picker
 * (one mechanism; design principle 2). The server owns every decision
 * (principle 4): GET /placements/<id>/sizing says what the size would need,
 * PATCH /placements/<id>/ {width_in, height_in[, location]} writes it, and
 * the override is the library's own per-size
 * POST /decorations/<id>/approve-on-behalf.
 *
 * API
 * ===
 *   const flow = usePlacementSizeChange({ refresh })
 *     refresh: async () => boolean -- reload the editor's server state.
 *
 *   flow.apply({ placementId, location?, size?, fromLabel? })
 *       -> Promise<{ status: "applied" | "kept" | "cancelled" | "error", error?, httpStatus?, warning? }>
 *     warning: the size changed but a follow-up did not (the override's
 *       approval failed on a store that is not live); show it.
 *     location: { view_code } | { placement_zone_id } -- a move. With no
 *       ``size`` the size becomes the new location's default (the size a
 *       new placement of the design gets there).
 *     size: { width, height } -- a resize (or the size to move with).
 *     fromLabel: the current location's label; a move that changes the size
 *       remembers the old size under it, for "switch back".
 *   flow.dialog  reactive { open, check, busy, error, isMove }
 *     The approval dialog's state. Open only while the store's client owes
 *     an approval for the resulting size; resolve it with:
 *   flow.keep()      move without changing the size (only a move, and only
 *                    when the current size fits the new location)
 *   flow.proceed()   use the size; it goes to the client for approval
 *                    later (only a store that is not live)
 *   flow.override({ reasonId, notes })  approve the size on the client's
 *                    behalf, then use it
 *   flow.cancel()    change nothing
 *
 *   previousSizeFor(placementId) -> { width, height, label } | null
 *     The size a move replaced (reactive; module-wide so the inspector's
 *     size editor and its location picker share it).
 *   forgetPreviousSize(placementId)
 *   formatSize(width, height) -> "4 x 4 in"
 */
import { reactive } from "vue";
import api from "@/services/api";

const previousSizes = reactive({});

export function previousSizeFor(placementId) {
    return previousSizes[placementId] ?? null;
}

export function forgetPreviousSize(placementId) {
    delete previousSizes[placementId];
}

// Tests only.
export function _resetPreviousSizes() {
    for (const key of Object.keys(previousSizes)) delete previousSizes[key];
}

// "4 x 4 in" -- a size as the inspector and the dialog word it.
export function formatSize(width, height) {
    const n = (v) => String(Number(v));
    return `${n(width)} x ${n(height)} in`;
}

// Server copy for a 4xx (the endpoints word their refusals for the user);
// never a 5xx body, which is written for operators.
export function sizeErrorMessage(err, fallback = "Couldn't change the size. Try again.") {
    const status = err?.response?.status;
    const serverMsg = err?.response?.data?.error;
    if (status >= 400 && status < 500 && typeof serverMsg === "string" && serverMsg) {
        return serverMsg;
    }
    return fallback;
}

// A refused request as apply() reports it: the user-facing copy, and the
// HTTP status so a caller can react (e.g. reload after a 409).
function refused(err, fallback) {
    const result = { status: "error", error: sizeErrorMessage(err, fallback) };
    const httpStatus = err?.response?.status;
    if (httpStatus) result.httpStatus = httpStatus;
    return result;
}

export function usePlacementSizeChange({ refresh } = {}) {
    const dialog = reactive({
        open: false,
        check: null,
        busy: false,
        error: "",
        isMove: false,
    });

    // The pending request the dialog is about.
    let pending = null;

    async function reload() {
        if (typeof refresh !== "function") return true;
        try {
            return (await refresh()) !== false;
        } catch (_err) {
            return false;
        }
    }

    function writeBody(req, check, { withSize }) {
        const body = { ...(req.location || {}) };
        if (withSize) {
            body.width_in = check.candidate.width_in;
            body.height_in = check.candidate.height_in;
        }
        return body;
    }

    function remember(req, check, wrote) {
        if (!req.location || !wrote) return;
        previousSizes[req.placementId] = {
            width: check.current.width_in,
            height: check.current.height_in,
            label: req.fromLabel || "",
        };
    }

    async function write(req, check, { withSize }) {
        const resp = await api.patch(
            `/placements/${req.placementId}/`,
            writeBody(req, check, { withSize }),
        );
        return resp?.data?.size ?? null;
    }

    async function settle(req, check, { withSize }) {
        remember(req, check, withSize);
        await reload();
    }

    async function apply(req) {
        const params = { ...(req.location || {}) };
        if (req.size) {
            params.width_in = req.size.width;
            params.height_in = req.size.height;
        }
        const isMove = Boolean(req.location);
        const fallback = isMove
            ? "Couldn't change the location. Try again."
            : "Couldn't change the size. Try again.";
        let check;
        try {
            const resp = await api.get(`/placements/${req.placementId}/sizing`, { params });
            check = resp?.data;
        } catch (err) {
            return refused(err, fallback);
        }
        if (!check || !check.candidate) {
            return { status: "error", error: fallback };
        }
        const sizeChanges = !check.fixed_size && !check.candidate.is_current;

        if (isMove && !sizeChanges) {
            return moveOnly(req, check);
        }
        if (!sizeChanges) return { status: "applied" };
        if (check.candidate.conflict) {
            // The server's own refusal (placement_resize.OTHER_VERSION_MESSAGE):
            // that size exists, but on another version of the artwork.
            return {
                status: "error",
                error: "That size of this design exists on a different version of the artwork. "
                    + "Choose another size, or replace the artwork first.",
            };
        }
        if (!check.candidate.fits) {
            const loc = check.location || {};
            return {
                status: "error",
                error: `That size is larger than this location allows (${loc.max_width_in} x ${loc.max_height_in} in).`,
            };
        }
        if (check.candidate.fits_shape === false) {
            // The server would refuse it 422 size_aspect_mismatch; say so
            // before writing or asking for an approval of it.
            return {
                status: "error",
                error: check.candidate.shape_message
                    || "That size would stretch the artwork. Keep its shape.",
            };
        }
        if (check.candidate.resolution && check.candidate.resolution.blocked) {
            // The method's minimum DPI (card #573): the server would refuse it
            // 422 dpi_below_minimum, with this same sentence. Say so before
            // writing or asking for an approval of it.
            return {
                status: "error",
                error: check.candidate.resolution.message
                    || "This image is too small to print well at that size.",
                httpStatus: 422,
            };
        }
        if (!check.candidate.needs_approval) {
            try {
                await write(req, check, { withSize: true });
            } catch (err) {
                return refused(err, fallback);
            }
            await settle(req, check, { withSize: true });
            return { status: "applied" };
        }
        return askFirst(req, check, isMove);
    }

    async function moveOnly(req, check) {
        try {
            await write(req, check, { withSize: false });
        } catch (err) {
            return refused(err, "Couldn't change the location. Try again.");
        }
        await reload();
        return { status: "applied" };
    }

    function askFirst(req, check, isMove) {
        return new Promise((resolve) => {
            pending = { req, check, resolve };
            dialog.check = check;
            dialog.isMove = isMove;
            dialog.error = "";
            dialog.busy = false;
            dialog.open = true;
        });
    }

    function finish(result) {
        const p = pending;
        pending = null;
        dialog.open = false;
        dialog.busy = false;
        dialog.check = null;
        dialog.error = "";
        if (p) p.resolve(result);
    }

    function canKeep() {
        return Boolean(pending && pending.req.location && pending.check.current?.fits);
    }

    function canProceed() {
        return Boolean(pending && !pending.check.store?.live);
    }

    async function keep() {
        if (!canKeep() || dialog.busy) return;
        const { req, check } = pending;
        dialog.busy = true;
        dialog.error = "";
        try {
            await write(req, check, { withSize: false });
        } catch (err) {
            dialog.busy = false;
            dialog.error = sizeErrorMessage(err, "Couldn't change the location. Try again.");
            return;
        }
        await reload();
        finish({ status: "kept" });
    }

    async function proceed() {
        if (!canProceed() || dialog.busy) return;
        const { req, check } = pending;
        dialog.busy = true;
        dialog.error = "";
        try {
            await write(req, check, { withSize: true });
        } catch (err) {
            dialog.busy = false;
            dialog.error = sizeErrorMessage(err);
            return;
        }
        await settle(req, check, { withSize: true });
        finish({ status: "applied" });
    }

    async function override({ reasonId, notes } = {}) {
        if (!pending || dialog.busy) return;
        const { req, check } = pending;
        const clientId = check.store?.client?.id;
        const text = String(notes || "").trim();
        if (clientId == null || reasonId == null || !text) return;
        dialog.busy = true;
        dialog.error = "";
        const keepOpen = async (err, fallback) => {
            dialog.busy = false;
            dialog.error = sizeErrorMessage(err, fallback);
            // A held write may have created the size; show what is saved.
            await reload();
        };

        // A live store HOLDS an unapproved size: the write creates the size's
        // decoration but binds nothing (``size.held``). A store that is not
        // live binds it at once. Either way the size now exists to approve.
        let size;
        try {
            size = await write(req, check, { withSize: true });
        } catch (err) {
            await keepOpen(err);
            return;
        }
        const held = size?.held ?? null;
        if (check.store?.live && !held) {
            // Bound on a live store: already approved (e.g. a retry after the
            // approval landed but the bind did not).
            await settle(req, check, { withSize: true });
            finish({ status: "applied" });
            return;
        }
        const decorationId = held?.decoration_id ?? size?.decoration_id;
        try {
            if (decorationId == null) throw new Error("no decoration");
            await api.post(`/decorations/${decorationId}/approve-on-behalf`, {
                client_id: clientId,
                reason_id: Number(reasonId),
                notes: text,
            });
        } catch (err) {
            if (held) {
                await keepOpen(err, "Couldn't approve this size. Try again.");
                return;
            }
            // Not live: the size is already in place, still awaiting the
            // client -- as if the user had chosen "approve later".
            await settle(req, check, { withSize: true });
            finish({
                status: "applied",
                warning: "The size was changed, but approving it on the client's behalf failed: "
                    + sizeErrorMessage(err, "try again from the decoration library."),
            });
            return;
        }
        if (held) {
            try {
                // Approved now: the same write binds it.
                await write(req, check, { withSize: true });
            } catch (err) {
                await keepOpen(err);
                return;
            }
        }
        await settle(req, check, { withSize: true });
        finish({ status: "applied" });
    }

    function cancel() {
        if (dialog.busy) return;
        finish({ status: "cancelled" });
    }

    return { apply, dialog, keep, proceed, override, cancel, canKeep, canProceed };
}
